import { useState, useEffect, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { 
  Receipt, CheckCircle, Clock,
  Banknote, AlertTriangle, Calendar,
  TrendingUp, TrendingDown, PieChart, Package, Truck, Users
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

// Chart colors
const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#6b7280'];

// Skeleton loading component
function SkeletonCard() {
  return (
    <Card className="relative overflow-hidden p-4 sm:p-5 border-0 rounded-2xl bg-slate-100">
      <div className="space-y-3">
        <div className="w-10 h-10 rounded-xl skeleton" />
        <div className="w-20 h-3 rounded skeleton" />
        <div className="w-32 h-6 rounded skeleton" />
        <div className="w-24 h-3 rounded skeleton" />
      </div>
    </Card>
  );
}

function SkeletonChart() {
  return (
    <Card className="p-4 sm:p-6 border-0 rounded-2xl shadow-lg bg-white">
      <div className="h-64 sm:h-80 flex items-center justify-center">
        <div className="w-full h-full skeleton rounded-xl" />
      </div>
    </Card>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, color, onClick, animationDelay = 0 }) {
  // Check if value is a large number to adjust text size
  const isLargeValue = value && value.toString().length > 12;
  
  return (
    <Card 
      className={`relative overflow-hidden p-4 sm:p-5 border-0 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group ${color} animate-fade-in-up opacity-0`}
      style={{ animationDelay: `${animationDelay}ms`, animationFillMode: 'forwards' }}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onClick}
    >
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <div className="p-2 rounded-xl bg-white/20 backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={1.5} />
          </div>
        </div>
        <p className="text-white/70 text-xs sm:text-sm font-medium mb-1 truncate">{title}</p>
        <p className={`text-white font-bold tracking-tight truncate ${isLargeValue ? 'text-base sm:text-lg' : 'text-lg sm:text-2xl'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
          {value}
        </p>
        {subtitle && (
          <p className="text-white/60 text-xs mt-1 truncate">{subtitle}</p>
        )}
      </div>
      <div className="absolute -right-4 -bottom-4 w-20 sm:w-24 h-20 sm:h-24 rounded-full bg-white/10 group-hover:scale-125 transition-transform duration-500" />
    </Card>
  );
}

function SectionHeader({ title, subtitle, icon: Icon }) {
  return (
    <div className="mb-4 flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-slate-500" />}
      <div>
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">{title}</h3>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function formatMonth(monthStr) {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month) - 1]} '${year.slice(2)}`;
}

function formatCurrencyShort(amount) {
  const num = Math.abs(amount || 0);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(2)}K`;
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="text-sm font-medium text-slate-700 mb-1">{formatMonth(label)}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: ₹{(entry.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [accountBalance, setAccountBalance] = useState(null);
  const [dashSummary, setDashSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartsReady, setChartsReady] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Scroll to top on mount - simplified for mobile (iOS fix)
  useEffect(() => {
    // Disable browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Single scroll to top - avoid multiple timers that can block touch events
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    // Use AbortController to cancel requests when component unmounts (navigating away)
    const abortController = new AbortController();
    
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const signal = abortController.signal;
        
        const [statsRes, chartRes, profitRes, summaryRes] = await Promise.all([
          axios.get(`${API}/expenses/summary/stats`, { headers, signal, timeout: 15000 }),
          axios.get(`${API}/expenses/dashboard/charts`, { headers, signal, timeout: 15000 }),
          axios.get(`${API}/profit-report`, { headers, signal, timeout: 15000 }),
          axios.get(`${API}/profit-report/dashboard-summary`, { headers, signal, timeout: 15000 }),
        ]);
        
        // Only update state if not aborted
        if (!abortController.signal.aborted) {
          startTransition(() => {
            setStats(statsRes.data);
            setChartData(chartRes.data);
            setAccountBalance(profitRes.data?.account_balance);
            setDashSummary(summaryRes.data);
          });
        }
      } catch (error) {
        // Ignore abort errors (user navigated away)
        if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
          return;
        }
        console.error('Dashboard data error:', error);
        if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
          toast.error('Network error - please check your connection');
        } else if (error.response?.status >= 500) {
          toast.error('Server temporarily unavailable - please try again');
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    // Cleanup: abort requests when navigating away
    return () => {
      abortController.abort();
    };
  }, []);

  // Delay chart rendering to allow UI to be interactive first
  useEffect(() => {
    if (!loading && dashSummary) {
      const timer = setTimeout(() => {
        setChartsReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, dashSummary]);

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    const abs = Math.abs(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return num < 0 ? `-₹${abs}` : `₹${abs}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]" style={{ touchAction: 'auto' }}>
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-container">
        {/* Header */}
        <div>
          <p className="text-slate-500 text-sm font-semibold tracking-wider uppercase mb-1">Overview</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Welcome back, {user.name?.split(' ')[0] || 'User'}
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Here's what's happening with your business</p>
        </div>

        {/* ── 1. FINANCIAL SUMMARY (all-time) ───────────────────────────── */}
        <div>
          <SectionHeader title="Financial Summary" subtitle="From company beginning till date" icon={Banknote} />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <StatCard
              title="Total Income (Paid + Pending)"
              value={formatCurrency(dashSummary?.financial_summary?.total_income_paid_pending)}
              subtitle="All accepted loads"
              icon={TrendingUp}
              color="bg-gradient-to-br from-emerald-500 to-emerald-600"
              onClick={() => navigate('/silica-loads')}
              animationDelay={0}
            />
            <StatCard
              title="Total Income (Paid Only)"
              value={formatCurrency(dashSummary?.financial_summary?.total_income_paid_only)}
              subtitle="Settled & paid loads"
              icon={CheckCircle}
              color="bg-gradient-to-br from-blue-500 to-blue-600"
              onClick={() => navigate('/silica-loads')}
              animationDelay={100}
            />
            <StatCard
              title="Total Expenses"
              value={formatCurrency(dashSummary?.financial_summary?.total_expenses)}
              subtitle="All time expenses"
              icon={Receipt}
              color="bg-gradient-to-br from-slate-700 to-slate-800"
              onClick={() => navigate('/expenses')}
              animationDelay={200}
            />
          </div>

          {/* Net Profit cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
            {/* Net Profit - Paid + Pending */}
            <Card className={`p-5 border-0 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer animate-fade-in-up opacity-0 ${
              (dashSummary?.financial_summary?.net_profit_paid_pending ?? 0) >= 0
                ? 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                : 'bg-gradient-to-br from-red-500 to-red-600'
            }`}
              style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
              onClick={() => navigate('/reports')}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-xl bg-white/20">
                  {(dashSummary?.financial_summary?.net_profit_paid_pending ?? 0) >= 0
                    ? <TrendingUp className="w-5 h-5 text-white" />
                    : <TrendingDown className="w-5 h-5 text-white" />}
                </div>
                <span className="text-xs font-semibold text-white/70 bg-white/10 px-2 py-1 rounded-full">Paid + Pending</span>
              </div>
              <p className="text-white/70 text-xs font-medium mb-1">Net Profit</p>
              <p className="text-white text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {formatCurrency(dashSummary?.financial_summary?.net_profit_paid_pending)}
              </p>
              <p className="text-white/60 text-xs mt-1">Income (paid + pending) minus all expenses</p>
            </Card>

            {/* Net Profit - Paid Only */}
            <Card className={`p-5 border-0 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer animate-fade-in-up opacity-0 ${
              (dashSummary?.financial_summary?.net_profit_paid_only ?? 0) >= 0
                ? 'bg-gradient-to-br from-violet-500 to-violet-600'
                : 'bg-gradient-to-br from-slate-600 to-slate-700'
            }`}
              style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}
              onClick={() => navigate('/reports')}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-xl bg-white/20">
                  {(dashSummary?.financial_summary?.net_profit_paid_only ?? 0) >= 0
                    ? <TrendingUp className="w-5 h-5 text-white" />
                    : <TrendingDown className="w-5 h-5 text-white" />}
                </div>
                <span className="text-xs font-semibold text-white/70 bg-white/10 px-2 py-1 rounded-full">Paid Only</span>
              </div>
              <p className="text-white/70 text-xs font-medium mb-1">Net Profit</p>
              <p className="text-white text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {formatCurrency(dashSummary?.financial_summary?.net_profit_paid_only)}
              </p>
              <p className="text-white/60 text-xs mt-1">Paid income only minus all expenses</p>
            </Card>
          </div>
        </div>

        {/* ── 2. EXPENSES OVERVIEW ──────────────────────────────────────── */}
        <div>
          <SectionHeader title="Expenses Overview" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              title="Total Expenses"
              value={formatCurrency(stats?.total_expenses)}
              subtitle={`${stats?.total_count || 0} transactions`}
              icon={Receipt}
              color="bg-gradient-to-br from-slate-800 to-slate-900"
              onClick={() => navigate('/expenses')}
              animationDelay={0}
            />
            <StatCard
              title="This Month"
              value={formatCurrency(stats?.current_month_total)}
              subtitle={`${stats?.current_month_count || 0} expenses`}
              icon={Calendar}
              color="bg-gradient-to-br from-blue-500 to-blue-600"
              onClick={() => navigate('/expenses')}
              animationDelay={100}
            />
            <StatCard
              title="Approved"
              value={formatCurrency(stats?.approved_amount)}
              subtitle={`${stats?.approved_count || 0} total | ${stats?.current_month_approved_count || 0} this month`}
              icon={CheckCircle}
              color="bg-gradient-to-br from-emerald-500 to-emerald-600"
              onClick={() => navigate('/expenses')}
              animationDelay={200}
            />
            <StatCard
              title="Pending"
              value={formatCurrency(stats?.pending_expense_amount)}
              subtitle={`${stats?.pending_count || 0} total | ${stats?.current_month_pending_count || 0} this month`}
              icon={Clock}
              color="bg-gradient-to-br from-slate-400 to-slate-500"
              onClick={() => navigate('/expenses')}
              animationDelay={300}
            />
          </div>
        </div>

        {/* ── 3. SILICA LOADS SUMMARY ───────────────────────────────────── */}
        <div>
          <SectionHeader title="Silica Loads Summary" subtitle="All-time loads overview" icon={Truck} />
          {/* Top stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              title="Total Purchased"
              value={`${(dashSummary?.silica_summary?.total_purchased_tons || 0).toLocaleString('en-IN')} T`}
              subtitle={formatCurrency(dashSummary?.silica_summary?.total_purchased_amount)}
              icon={Package}
              color="bg-gradient-to-br from-slate-500 to-slate-600"
              onClick={() => navigate('/expenses')}
              animationDelay={0}
            />
            <StatCard
              title="Total Sent"
              value={`${(dashSummary?.silica_summary?.total_sent_tons || 0).toLocaleString('en-IN')} T`}
              subtitle="All dispatched loads"
              icon={Truck}
              color="bg-gradient-to-br from-blue-500 to-blue-600"
              onClick={() => navigate('/silica-loads')}
              animationDelay={100}
            />
            <StatCard
              title="Total Accepted"
              value={`${(dashSummary?.silica_summary?.total_accepted_tons || 0).toLocaleString('en-IN')} T`}
              subtitle="Client-accepted loads"
              icon={CheckCircle}
              color="bg-gradient-to-br from-emerald-500 to-emerald-600"
              onClick={() => navigate('/silica-loads')}
              animationDelay={200}
            />
            <StatCard
              title="Total Rejected"
              value={`${(dashSummary?.silica_summary?.total_rejected_tons || 0).toLocaleString('en-IN')} T`}
              subtitle="Rejected by client"
              icon={AlertTriangle}
              color="bg-gradient-to-br from-red-500 to-red-600"
              onClick={() => navigate('/silica-loads')}
              animationDelay={300}
            />
          </div>

          {/* Client breakdown table */}
          {dashSummary?.silica_summary?.client_breakdown?.length > 0 && (
            <Card className="mt-4 p-5 border-0 rounded-2xl shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-slate-500" />
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Client-wise Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase">Client</th>
                      <th className="px-3 py-2 text-right text-xs font-bold text-slate-500 uppercase">Total Tons</th>
                      <th className="px-3 py-2 text-right text-xs font-bold text-emerald-600 uppercase">Accepted</th>
                      <th className="px-3 py-2 text-right text-xs font-bold text-red-500 uppercase">Rejected</th>
                      <th className="px-3 py-2 text-right text-xs font-bold text-slate-500 uppercase">Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashSummary.silica_summary.client_breakdown.map((c, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {c.client_name?.charAt(0) || '?'}
                            </div>
                            <span className="font-medium text-slate-900">{c.client_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-slate-900">{c.total_tons.toLocaleString('en-IN', { maximumFractionDigits: 2 })} T</td>
                        <td className="px-3 py-3 text-right font-semibold text-emerald-600">{c.accepted_tons.toLocaleString('en-IN', { maximumFractionDigits: 2 })} T</td>
                        <td className="px-3 py-3 text-right font-semibold text-red-500">{c.rejected_tons.toLocaleString('en-IN', { maximumFractionDigits: 2 })} T</td>
                        <td className="px-3 py-3 text-right font-semibold text-slate-500">{c.pending_tons.toLocaleString('en-IN', { maximumFractionDigits: 2 })} T</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>

        {/* Charts Section - Delayed rendering for better interactivity */}
        {chartsReady && chartData && (
          <>
            {/* Income vs Expense Chart */}
            <div className="chart-animate">
              <SectionHeader title="Income vs Expense Trend" subtitle="Last 6 months" icon={TrendingUp} />
              <Card className="p-4 sm:p-6 border-0 rounded-2xl shadow-lg bg-white hover:shadow-xl transition-shadow duration-300">
                <div className="h-64 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData.income_vs_expense} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="month" 
                        tickFormatter={formatMonth} 
                        tick={{ fontSize: 12, fill: '#64748b' }}
                        axisLine={{ stroke: '#e2e8f0' }}
                      />
                      <YAxis 
                        tickFormatter={formatCurrencyShort} 
                        tick={{ fontSize: 12, fill: '#64748b' }}
                        axisLine={{ stroke: '#e2e8f0' }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend 
                        wrapperStyle={{ paddingTop: '10px' }}
                        formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
                      />
                      <Bar dataKey="income" name="Income" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="expense" name="Expense" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>

            {/* Two Column Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Expense Trend Area Chart */}
              <div>
                <SectionHeader title="Expense Trend" subtitle="Monthly spending" />
                <Card className="p-4 sm:p-6 border-0 rounded-2xl shadow-lg bg-white">
                  <div className="h-56 sm:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData.expense_trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="month" 
                          tickFormatter={formatMonth}
                          tick={{ fontSize: 11, fill: '#64748b' }}
                        />
                        <YAxis 
                          tickFormatter={formatCurrencyShort}
                          tick={{ fontSize: 11, fill: '#64748b' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                          type="monotone" 
                          dataKey="amount" 
                          name="Expenses"
                          stroke="#f59e0b" 
                          strokeWidth={2}
                          fill="url(#expenseGradient)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </div>

              {/* Category Pie Chart */}
              <div>
                <SectionHeader title="Expenses by Category" subtitle="All time breakdown" icon={PieChart} />
                <Card className="p-4 sm:p-6 border-0 rounded-2xl shadow-lg bg-white">
                  <div className="h-56 sm:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={chartData.expense_by_category}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="amount"
                          nameKey="category"
                          label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                          labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                        >
                          {chartData.expense_by_category?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => [`₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Amount']}
                        />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* App Version */}
      <p className="text-center text-xs text-slate-400 mt-8 mb-4">App Version: 2.2.0 (Feb 28, 2026)</p>
    </Layout>
  );
}
